/**
 * Live coordinate readout (parent spec §11 Location panel, AC-N4): current
 * easting/northing, elevation AOD, bearing, eye height, FOV. This module
 * is the pure compute/format half; ui/coordinateReadoutPanel.ts wires it
 * to the DOM.
 */
import { bearingDegFromForward, localToBng } from '../core/coordinates';
import { focalLengthMmEquivalent } from '../camera/viewScale';

export interface ReadoutState {
  eastingM: number;
  northingM: number;
  elevationAodM: number;
  bearingDeg: number;
  /** Live camera vertical FOV, degrees (issue #95) — presentation-only,
   * never fed into any LOS result (hard constraint #1). */
  fovDeg: number;
}

/**
 * Computes the readout from a camera's local-space position (x, y, z), its
 * horizontal forward direction (forwardDx, forwardDz — need not be
 * normalised), and its current vertical FOV in degrees.
 */
export function computeReadout(
  x: number,
  y: number,
  z: number,
  forwardDx: number,
  forwardDz: number,
  fovDeg: number,
): ReadoutState {
  const { easting, northing } = localToBng(x, z);
  return {
    eastingM: easting,
    northingM: northing,
    elevationAodM: y,
    bearingDeg: bearingDegFromForward(forwardDx, forwardDz),
    fovDeg,
  };
}

export function formatReadout(r: ReadoutState): string {
  return [
    `E ${r.eastingM.toFixed(1)}`,
    `N ${r.northingM.toFixed(1)}`,
    `${r.elevationAodM.toFixed(1)} m AOD`,
    `bearing ${r.bearingDeg.toFixed(0)}°`,
    `FOV ${r.fovDeg.toFixed(0)}° (${focalLengthMmEquivalent(r.fovDeg)}mm)`,
  ].join('  |  ');
}
